import { parseDateString } from '../utils/dateString';
// Profile Types for NEXEDU - E-DEP Compatible

// Sexe: Display values vs stored values
export type SexeDisplay = 'Masculin' | 'Féminin';
export type SexeValue = 'M' | 'F';

export const sexeMapping: Record<SexeDisplay, SexeValue> = {
  'Masculin': 'M',
  'Féminin': 'F',
};

export const sexeReverseMapping: Record<SexeValue, SexeDisplay> = {
  'M': 'Masculin',
  'F': 'Féminin',
};

// Handicap Types (E-DEP nomenclature)
export type HandicapValue = 'N' | 'A' | 'M' | 'V' | 'AM' | 'AV' | 'MV' | 'T' | 'XX';

export interface HandicapOption {
  value: HandicapValue;
  label: string;
  description: string;
}

export const handicapOptions: HandicapOption[] = [
  { value: 'N', label: 'Aucun', description: 'Pas de handicap' },
  { value: 'A', label: 'Auditif', description: 'Handicap auditif' },
  { value: 'M', label: 'Moteur', description: 'Handicap moteur' },
  { value: 'V', label: 'Visuel', description: 'Handicap visuel' },
  { value: 'AM', label: 'Auditif + Moteur', description: 'Handicap auditif et moteur' },
  { value: 'AV', label: 'Auditif + Visuel', description: 'Handicap auditif et visuel' },
  { value: 'MV', label: 'Moteur + Visuel', description: 'Handicap moteur et visuel' },
  { value: 'T', label: 'Total', description: 'Handicap total (auditif, moteur et visuel)' },
  { value: 'XX', label: 'Autre', description: 'Autre type de handicap' },
];

// Document type for ID scanning
export type DocumentType = 'CIN' | 'PASSPORT_MA' | 'PASSPORT_EU';

export interface ScannedDocumentData {
  documentType: DocumentType;
  firstName?: string;
  lastName?: string;
  firstNameArabic?: string;
  lastNameArabic?: string;
  dateOfBirth?: string; // DD/MM/YYYY
  gender?: SexeValue;
  documentNumber?: string; // CIN or Passport number
  countryOfOrigin?: string; // Country code
  expiryDate?: string;
  nationality?: string;
  placeOfBirth?: string;
  rawText?: string; // Raw OCR text for debugging
  confidence?: number; // OCR confidence score
}

// Common user profile fields (E-DEP compatible)
export interface CommonProfileFields {
  // Identity
  email: string;
  cnie?: string; // For Moroccans
  passport?: string; // For foreigners
  nom: string;
  prenom: string;
  nomArabe: string;
  prenomArabe: string;

  // Personal info
  telephone?: string;
  phoneCountry?: string;
  sexe: SexeValue;
  dateNaissance: string; // DD/MM/YYYY format
  paysOrigine: string; // Country code from nomenclature
  handicap: HandicapValue;

  // Address
  adresse?: string;

  // Emergency contact
  urgenceNom?: string;
  urgenceRelation?: string;
  urgencePhoneCountry?: string;
  urgenceNumero?: string;
  urgenceAdresse?: string;

  // Profile photo
  photo?: string;

  // Staff-specific professional info (optional)
  rib?: string;
  salaireMensuelNet?: string;
  fonction?: string;
  diplomeId?: string;
  dateRecrutement?: string;
}

// Required documents
export interface ProfileDocuments {
  cinScan?: File | string; // Required for Moroccans
  passportScan?: File | string; // Required for foreigners
  cv?: File | string; // Required except for students
  photo?: File | string;
}

// Student-specific fields
export interface StudentProfileFields extends CommonProfileFields {
  // Academic info
  cne?: string; // Code National de l'Étudiant
  baccalaureat?: {
    annee: string;
    serie: string;
    mention?: string;
    moyenne?: number;
    etablissement?: string;
    ville?: string;
  };
  // Parent/Guardian info
  parentNom?: string;
  parentPrenom?: string;
  parentTelephone?: string;
  parentEmail?: string;
  parentProfession?: string;
}

// Professor status types for E-DEP
export type StatutProfesseur = 'P' | 'V'; // P = Permanent, V = Vacataire
export type EstOriginaireEtablissementPublic = 'O' | 'N'; // O = Oui, N = Non

// Teacher-specific fields (E-DEP compliant)
export interface TeacherProfileFields extends CommonProfileFields {
  // E-DEP Professor fields
  statutProfesseur?: StatutProfesseur;
  departementAttacheId?: string; // ID from NomenclatureDepartement
  estOriginaireEtablissementPublic?: EstOriginaireEtablissementPublic; // Required if vacataire
  etablissementOrigine?: string; // Required if vacataire
  gradeId?: string; // ID from NomenclatureGrade
  specialite?: string; // Free text specialty
  matieresEnseignees?: string; // Auto-generated from course assignments
  tauxHoraire?: string; // Hourly rate in MAD - required if vacataire

  // Legacy fields (kept for compatibility)
  statutProfessionnel?: 'permanent' | 'vacataire' | 'visiteur';
  grade?: string;
  // Academic qualifications
  diplomes?: {
    titre: string;
    etablissement: string;
    annee: string;
    pays: string;
  }[];
}

// Professor document types
export type ProfessorDocumentType =
  | 'AUTORISATION_ENSEIGNER' // For vacataires from public institutions - renewable annually
  | 'ENGAGEMENT_PROFESSEUR'; // Signed engagement - renewable annually (for all professors)

// Staff-specific fields
export interface StaffProfileFields extends CommonProfileFields {
  fonction: string;
  departement?: string;
  dateRecrutement?: string;
  statutContrat: 'CDI' | 'CDD' | 'stage';
}

// Parent-specific fields
export interface ParentProfileFields extends CommonProfileFields {
  profession?: string;
  entreprise?: string;
  // Children linked to this parent
  enfants?: string[]; // Array of student IDs
}

// Full profile type union
export type UserProfile =
  | { type: 'student'; data: StudentProfileFields }
  | { type: 'teacher'; data: TeacherProfileFields }
  | { type: 'staff'; data: StaffProfileFields }
  | { type: 'parent'; data: ParentProfileFields }
  | { type: 'admin'; data: CommonProfileFields };

// Form validation rules
export const profileValidation = {
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  },
  cnie: {
    required: false, // Required only for Moroccans
    pattern: /^[A-Z]{1,2}[0-9]{5,7}$/, // Moroccan CIN format
  },
  passport: {
    required: false, // Required only for foreigners
    pattern: /^[A-Z0-9]{6,12}$/, // General passport format
  },
  telephone: {
    required: false,
    pattern: /^(\+212|0)[5-7][0-9]{8}$/, // Moroccan phone format
  },
  dateNaissance: {
    required: true,
    minAge: 16,
    maxAge: 90,
  },
};

// Helper function to convert display value to stored value
export function toStoredSexe(display: SexeDisplay): SexeValue {
  return sexeMapping[display];
}

// Helper function to convert stored value to display value
export function toDisplaySexe(stored: SexeValue): SexeDisplay {
  return sexeReverseMapping[stored];
}

// Helper to format date from various formats to DD/MM/YYYY
export function formatDateToDDMMYYYY(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// Helper to parse DD/MM/YYYY to Date
export function parseDDMMYYYY(dateStr: string): Date | null {
  // Accepts the stored "YYYY-MM-DD" form as well as the typed "DD/MM/YYYY" form
  return parseDateString(dateStr);
}

// Calculate age from date string
export function calculateAge(dateStr: string): number {
  const birthDate = parseDDMMYYYY(dateStr);
  if (!birthDate) return 0;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}
